/**
 * 🌐 **CONFIGURACIÓN DE ENDPOINTS DE AUTENTICACIÓN**
 *
 * **Patrón Implementado**: Configuration Object + Constants Pattern
 *
 * Este archivo centraliza todas las URLs y configuraciones relacionadas
 * con los endpoints de autenticación del sistema FlexPOS.
 *
 * **Beneficios:**
 * - ✅ Configuración centralizada y fácil de mantener
 * - ✅ Type safety con TypeScript
 * - ✅ Reutilización consistente en todo el sistema
 * - ✅ Facilita cambios de endpoints sin tocar múltiples archivos
 */

// ===================================================
// 🔐 **ENDPOINTS DE AUTENTICACIÓN PRINCIPAL**
// ===================================================

/**
 * 🎯 **Endpoints Core de Autenticación**
 */
export const AUTH_ENDPOINTS = {
  // 🔑 Autenticación básica
  LOGIN: '/api/auth/login',
  LOGOUT: '/api/auth/logout',
  REFRESH: '/api/auth/refresh',

  // 📝 Registro y verificación
  REGISTER: '/api/auth/register',
  VERIFY_EMAIL: '/api/auth/verify-email',
  RESEND_VERIFICATION: '/api/auth/resend-verification',

  // 🔐 Recuperación de contraseña
  FORGOT_PASSWORD: '/api/auth/forgot-password',
  RESET_PASSWORD: '/api/auth/reset-password',
  VALIDATE_RESET_TOKEN: '/api/auth/validate-reset-token',

  // 🔒 Cambio de contraseña
  CHANGE_PASSWORD: '/api/auth/change-password',

  // ✅ Validación y estado
  VALIDATE_TOKEN: '/api/auth/validate',
  ME: '/api/auth/me',
  STATUS: '/api/auth/status',

  // 🔐 Autenticación de dos factores
  ENABLE_2FA: '/api/auth/2fa/enable',
  DISABLE_2FA: '/api/auth/2fa/disable',
  VERIFY_2FA: '/api/auth/2fa/verify',
  GENERATE_2FA_QR: '/api/auth/2fa/qr',

  // 📊 Sesiones y auditoría
  ACTIVE_SESSIONS: '/api/auth/sessions',
  TERMINATE_SESSION: '/api/auth/sessions/terminate',
  TERMINATE_ALL_SESSIONS: '/api/auth/sessions/terminate-all',
} as const;

// ===================================================
// 👤 **ENDPOINTS DE GESTIÓN DE USUARIOS**
// ===================================================

/**
 * 👥 **Endpoints de Usuarios**
 */
export const USER_ENDPOINTS = {
  BASE: '/api/users',

  // 📋 CRUD básico
  LIST: '/api/users',
  CREATE: '/api/users',
  GET_BY_ID: '/api/users/:id',
  UPDATE: '/api/users/:id',
  DELETE: '/api/users/:id',

  // 🔍 Búsqueda y filtrado
  SEARCH: '/api/users/search',
  WITH_ROLES: '/api/users/with-roles',
  BY_ROLE: '/api/users/by-role/:role',

  // ✅ Validaciones
  CHECK_USERNAME: '/api/users/check-username',
  CHECK_EMAIL: '/api/users/check-email',

  // 🔐 Gestión de estado
  ACTIVATE: '/api/users/:id/activate',
  DEACTIVATE: '/api/users/:id/deactivate',
  RESET_PASSWORD: '/api/users/:id/reset-password',

  // 👮 Roles y permisos
  GET_ROLES: '/api/users/:id/roles',
  ASSIGN_ROLES: '/api/users/:id/roles',
  REMOVE_ROLES: '/api/users/:id/roles',
  GET_PERMISSIONS: '/api/users/:id/permissions',
} as const;

// ===================================================
// 👮 **ENDPOINTS DE ROLES Y PERMISOS**
// ===================================================

/**
 * 🛡️ **Endpoints de Roles**
 */
export const ROLE_ENDPOINTS = {
  BASE: '/api/roles',

  // 📋 CRUD básico
  LIST: '/api/roles',
  CREATE: '/api/roles',
  GET_BY_ID: '/api/roles/:id',
  UPDATE: '/api/roles/:id',
  DELETE: '/api/roles/:id',

  // 🔐 Gestión de permisos
  GET_PERMISSIONS: '/api/roles/:id/permissions',
  ASSIGN_PERMISSIONS: '/api/roles/:id/permissions',
  REMOVE_PERMISSIONS: '/api/roles/:id/permissions',

  // 👥 Usuarios con rol
  GET_USERS: '/api/roles/:id/users',

  // ✅ Utilidades
  CHECK_NAME: '/api/roles/check-name',
} as const;

/**
 * 🔐 **Endpoints de Permisos**
 */
export const PERMISSION_ENDPOINTS = {
  BASE: '/api/permissions',

  // 📋 CRUD básico
  LIST: '/api/permissions',
  CREATE: '/api/permissions',
  GET_BY_ID: '/api/permissions/:id',
  UPDATE: '/api/permissions/:id',
  DELETE: '/api/permissions/:id',

  // 🎯 Permisos específicos
  MY_PERMISSIONS: '/api/permissions/my-permissions',
  USER_PERMISSIONS: '/api/permissions/user/:userId',
  CHECK_PERMISSION: '/api/permissions/check',

  // 🗂️ Agrupación
  BY_RESOURCE: '/api/permissions/by-resource',
  BY_MODULE: '/api/permissions/by-module/:module',

  // 🔄 Cache
  INVALIDATE_USER_CACHE: '/api/permissions/cache/invalidate/:userId',
  INVALIDATE_ALL_CACHE: '/api/permissions/cache/invalidate-all',
} as const;

// ===================================================
// 🧭 **ENDPOINTS DE MENÚ Y NAVEGACIÓN**
// ===================================================

/**
 * 📋 **Endpoints de Menú**
 */
export const MENU_ENDPOINTS = {
  BASE: '/api/menu',

  // 🎯 Acceso personalizado
  ACCESS: '/api/menu/access',
  USER_MENU: '/api/menu/user/:userId',
  ROLE_MENU: '/api/menu/role/:roleId',

  // 🏗️ Construcción de menú
  BUILD: '/api/menu/build',
  TREE: '/api/menu/tree',
  BREADCRUMB: '/api/menu/breadcrumb/:route',
} as const;

// ===================================================
// 📊 **ENDPOINTS DE AUDITORÍA Y LOGGING**
// ===================================================

/**
 * 📈 **Endpoints de Auditoría**
 */
export const AUDIT_ENDPOINTS = {
  BASE: '/api/audit',

  // 🔍 Logs de autenticación
  AUTH_LOGS: '/api/audit/auth',
  LOGIN_ATTEMPTS: '/api/audit/auth/login-attempts',
  SESSION_LOGS: '/api/audit/auth/sessions',

  // 👤 Actividad de usuarios
  USER_ACTIVITY: '/api/audit/users/:userId',
  RECENT_ACTIVITY: '/api/audit/recent',

  // 📊 Reportes
  SECURITY_REPORT: '/api/audit/reports/security',
  LOGIN_REPORT: '/api/audit/reports/login',
  SESSION_REPORT: '/api/audit/reports/sessions',
} as const;

// ===================================================
// ⚙️ **CONFIGURACIÓN DE ENDPOINTS**
// ===================================================

/**
 * 🔧 **Configuración General de API**
 */
export const API_CONFIG = {
  VERSION: 'v1',
  BASE_PATH: '/api',
  TIMEOUT: 10000,
  RETRY_ATTEMPTS: 3,

  // 🔐 Headers por defecto
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },

  // 📊 Configuración de paginación
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
} as const;

/**
 * 🌐 **URLs Públicas (sin autenticación)**
 */
export const PUBLIC_ENDPOINTS = [
  // 🔐 Autenticación
  AUTH_ENDPOINTS.LOGIN,
  AUTH_ENDPOINTS.REFRESH,
  AUTH_ENDPOINTS.REGISTER,
  AUTH_ENDPOINTS.FORGOT_PASSWORD,
  AUTH_ENDPOINTS.RESET_PASSWORD,
  AUTH_ENDPOINTS.VALIDATE_RESET_TOKEN,
  AUTH_ENDPOINTS.VERIFY_EMAIL,
  AUTH_ENDPOINTS.RESEND_VERIFICATION,
] as const;

/**
 * 🔒 **URLs que requieren roles específicos**
 */
export const ADMIN_ONLY_ENDPOINTS = [
  USER_ENDPOINTS.LIST,
  USER_ENDPOINTS.CREATE,
  USER_ENDPOINTS.DELETE,
  ROLE_ENDPOINTS.CREATE,
  ROLE_ENDPOINTS.DELETE,
  PERMISSION_ENDPOINTS.CREATE,
  PERMISSION_ENDPOINTS.DELETE,
  AUDIT_ENDPOINTS.AUTH_LOGS,
  AUDIT_ENDPOINTS.SECURITY_REPORT,
] as const;

// ===================================================
// 🛠️ **UTILIDADES DE ENDPOINTS**
// ===================================================

/**
 * 🎯 **Utilidad para construir URLs dinámicas**
 *
 * @param endpoint - Template de endpoint con parámetros
 * @param params - Objeto con valores para los parámetros
 * @returns URL construida con parámetros reemplazados
 *
 * @example
 * ```typescript
 * const url = buildEndpoint(USER_ENDPOINTS.GET_BY_ID, { id: '123' });
 * // Result: '/api/users/123'
 * ```
 */
export const buildEndpoint = (
  endpoint: string,
  params: Record<string, string | number> = {}
): string => {
  let url = endpoint;

  Object.entries(params).forEach(([key, value]) => {
    url = url.replace(`:${key}`, String(value));
  });

  return url;
};

/**
 * 🔍 **Verificar si un endpoint es público**
 *
 * @param endpoint - Endpoint a verificar
 * @returns true si el endpoint es público
 */
export const isPublicEndpoint = (endpoint: string): boolean => {
  return PUBLIC_ENDPOINTS.some((publicEndpoint) =>
    endpoint.includes(publicEndpoint)
  );
};

/**
 * 👑 **Verificar si un endpoint requiere permisos de admin**
 *
 * @param endpoint - Endpoint a verificar
 * @returns true si requiere permisos de administrador
 */
export const isAdminEndpoint = (endpoint: string): boolean => {
  return ADMIN_ONLY_ENDPOINTS.some((adminEndpoint) =>
    endpoint.includes(adminEndpoint)
  );
};

/**
 * 📋 **Obtener todos los endpoints por categoría**
 */
export const getEndpointsByCategory = () => ({
  auth: AUTH_ENDPOINTS,
  users: USER_ENDPOINTS,
  roles: ROLE_ENDPOINTS,
  permissions: PERMISSION_ENDPOINTS,
  menu: MENU_ENDPOINTS,
  audit: AUDIT_ENDPOINTS,
});

/**
 * 🔧 **Configuración completa de endpoints**
 */
export const ENDPOINTS_CONFIG = {
  auth: AUTH_ENDPOINTS,
  users: USER_ENDPOINTS,
  roles: ROLE_ENDPOINTS,
  permissions: PERMISSION_ENDPOINTS,
  menu: MENU_ENDPOINTS,
  audit: AUDIT_ENDPOINTS,
  api: API_CONFIG,
  utils: {
    buildEndpoint,
    isPublicEndpoint,
    isAdminEndpoint,
    getEndpointsByCategory,
  },
} as const;

// ===================================================
// 📝 **TYPES PARA TYPE SAFETY**
// ===================================================

/**
 * 🎯 **Tipos derivados para type safety**
 */
export type AuthEndpoint = (typeof AUTH_ENDPOINTS)[keyof typeof AUTH_ENDPOINTS];
export type UserEndpoint = (typeof USER_ENDPOINTS)[keyof typeof USER_ENDPOINTS];
export type RoleEndpoint = (typeof ROLE_ENDPOINTS)[keyof typeof ROLE_ENDPOINTS];
export type PermissionEndpoint =
  (typeof PERMISSION_ENDPOINTS)[keyof typeof PERMISSION_ENDPOINTS];
export type MenuEndpoint = (typeof MENU_ENDPOINTS)[keyof typeof MENU_ENDPOINTS];
export type AuditEndpoint =
  (typeof AUDIT_ENDPOINTS)[keyof typeof AUDIT_ENDPOINTS];

export type PublicEndpoint = (typeof PUBLIC_ENDPOINTS)[number];
export type AdminEndpoint = (typeof ADMIN_ONLY_ENDPOINTS)[number];

export type EndpointCategory =
  | 'auth'
  | 'users'
  | 'roles'
  | 'permissions'
  | 'menu'
  | 'audit';

/**
 * 📋 **Interface para configuración de endpoint**
 */
export interface EndpointConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  requiresAuth: boolean;
  requiredRoles?: string[];
  requiredPermissions?: string[];
  rateLimit?: number;
  timeout?: number;
}

/**
 * 🔄 **Parámetros de endpoint dinámico**
 */
export interface EndpointParams {
  [key: string]: string | number;
}

/**
 * 📊 **Configuración de consulta**
 */
export interface QueryConfig {
  page?: number;
  size?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  filters?: Record<string, any>;
}
